import { TrainingAPI } from "../../api/TrainingAPI";
import { audioMessage as message } from "../../Components/Common/Notifications/audioMessage";

const trainingAPI = new TrainingAPI();
export const CALENDAR_SET_SUBMIT_CLICKED = "[CALENDAR]_SET_SUBMIT_CLICKED";
export const OVERVIEW_TRACKER_SET_FILTER = "[OVERVIEW]_TRACKER_SET_FILTER";
export const CALENDAR_V2_MONTH_REQUESTED = "[CALENDAR]_V2_MONTH_REQUESTED";
export const CALENDAR_V2_MONTH_RECEIVED = "[CALENDAR]_V2_MONTH_RECEIVED";
export const CALENDAR_V2_MONTH_FAILED = "[CALENDAR]_V2_MONTH_FAILED";
export const CALENDAR_V2_DAY_REQUESTED = "[CALENDAR]_V2_DAY_REQUESTED";
export const CALENDAR_V2_DAY_RECEIVED = "[CALENDAR]_V2_DAY_RECEIVED";
export const CALENDAR_V2_DAY_FAILED = "[CALENDAR]_V2_DAY_FAILED";

export const setSubmitClicked = (isClicked) => ({
  type: CALENDAR_SET_SUBMIT_CLICKED,
  payload: isClicked,
});

export const setFilter = (data) => {
  return {
    type: OVERVIEW_TRACKER_SET_FILTER,
    data,
  };
};

/**
 * Month-grid cells for one of the dual calendars, from the slim /calendar/month
 * endpoint. Also clears submitClicked so the filter bar's Submit resolves once
 * both calendars have reloaded.
 *
 * start_date doubles as the request key: the reducer drops a response whose month
 * is no longer the one being viewed, so paging quickly can't land stale cells.
 *
 * `colors` is the team's baseline-average map, the same one /v3/year returned. It
 * is filter-scoped rather than month-scoped, so every month reports it; the
 * practice-plan table colours its Total row against it. The endpoint answers with
 * an empty ARRAY when its upstream fails, hence the object guard.
 */
export const getCalendarMonthV2 = ({
  calendarId, start_date, end_date, player_ids=[], positions=[], player_groups=[], player_statuses=[], sessions=[], flag_types=[]
}) => {
  return async (dispatch) => {
    dispatch({ type: CALENDAR_V2_MONTH_REQUESTED, calendarId, requestKey: start_date });
    try {
      const res = await trainingAPI.getCalendarMonth(start_date, end_date, player_ids, positions, player_groups, player_statuses, sessions, flag_types);
      const baselines = res?.colors && !Array.isArray(res.colors) ? res.colors : {};
      dispatch({ type: CALENDAR_V2_MONTH_RECEIVED, calendarId, requestKey: start_date, payload: res?.data ?? {}, colors: baselines });
    } catch (e) {
      console.error("Error loading calendar month data", e);
      message.error("Error loading calendar data");
      dispatch({ type: CALENDAR_V2_MONTH_FAILED, calendarId, requestKey: start_date });
    } finally {
      dispatch(setSubmitClicked(false));
    }
  };
};

/**
 * Per-metric detail for a single clicked date, from /calendar/day. `drillsOverride`
 * lets the future-day path show the saved practice plan's drills (which carry
 * durations) in place of the empty actual-drills list the endpoint returns.
 * Stale responses are dropped the same way getCalendarMonthV2's are.
 */
export const getCalendarDayV2 = ({
  calendarId, date, drillsOverride=null, player_ids=[], positions=[], player_groups=[], player_statuses=[], sessions=[], flag_types=[]
}) => {
  return async (dispatch) => {
    dispatch({ type: CALENDAR_V2_DAY_REQUESTED, calendarId, requestKey: date });
    try {
      const day = await trainingAPI.getCalendarDay(date, player_ids, positions, player_groups, player_statuses, sessions, flag_types);
      dispatch({
        type: CALENDAR_V2_DAY_RECEIVED,
        calendarId,
        requestKey: date,
        payload: drillsOverride ? { ...day, drills: drillsOverride } : day,
      });
    } catch (e) {
      console.error("Error loading calendar day data", e);
      message.error("Error loading day details");
      dispatch({ type: CALENDAR_V2_DAY_FAILED, calendarId, requestKey: date });
    }
  };
};

export const deletePracticePlanForDate = (dateString) => {
  return async () => {
    try {
      await trainingAPI.deletePracticePlanByDate(dateString);
      message.success(`Predictions deleted for date ${dateString}`);
      return true;
    } catch (e) {
      console.error("Error deleting practice plan", e, dateString);
      message.error("Error deleting practice plan");
      return false;
    }
  };
};
